import { FC } from 'react';
import { Typography } from '@mui/material';
import { ThemeMode, useThemeMode } from 'context/ThemeModeContext';
import {
  AppearanceCard,
  AppearanceCardsRow,
  AppearanceContainer,
  AppearanceThumbnail,
} from './ThemeModeControl.styles';

// SVG thumbnail for Light mode
const LightThumbnail: FC = () => (
  <svg width="110" height="78" viewBox="0 0 110 78" xmlns="http://www.w3.org/2000/svg">
    <rect width="110" height="78" fill="#f5f5f5" />
    <rect width="110" height="18" fill="#e0e0e0" />
    <circle cx="12" cy="9" r="4" fill="#ff5f57" />
    <circle cx="26" cy="9" r="4" fill="#febc2e" />
    <circle cx="40" cy="9" r="4" fill="#28c840" />
    <rect x="8" y="26" width="60" height="6" rx="3" fill="#d0d0d0" />
    <rect x="8" y="38" width="88" height="5" rx="2.5" fill="#e0e0e0" />
    <rect x="8" y="49" width="76" height="5" rx="2.5" fill="#e0e0e0" />
    <rect x="8" y="60" width="55" height="5" rx="2.5" fill="#e0e0e0" />
  </svg>
);

// SVG thumbnail for Dark mode
const DarkThumbnail: FC = () => (
  <svg width="110" height="78" viewBox="0 0 110 78" xmlns="http://www.w3.org/2000/svg">
    <rect width="110" height="78" fill="#1e1e1e" />
    <rect width="110" height="18" fill="#2d2d2d" />
    <circle cx="12" cy="9" r="4" fill="#ff5f57" />
    <circle cx="26" cy="9" r="4" fill="#febc2e" />
    <circle cx="40" cy="9" r="4" fill="#28c840" />
    <rect x="8" y="26" width="60" height="6" rx="3" fill="#4a4a4a" />
    <rect x="8" y="38" width="88" height="5" rx="2.5" fill="#3a3a3a" />
    <rect x="8" y="49" width="76" height="5" rx="2.5" fill="#3a3a3a" />
    <rect x="8" y="60" width="55" height="5" rx="2.5" fill="#3a3a3a" />
  </svg>
);

// SVG thumbnail for Auto (split light/dark)
const AutoThumbnail: FC = () => (
  <svg width="110" height="78" viewBox="0 0 110 78" xmlns="http://www.w3.org/2000/svg">
    {/* Light half (left) */}
    <rect width="55" height="78" fill="#f5f5f5" />
    <rect width="55" height="18" fill="#e0e0e0" />
    <circle cx="12" cy="9" r="4" fill="#ff5f57" />
    <circle cx="26" cy="9" r="4" fill="#febc2e" />
    <rect x="8" y="26" width="34" height="6" rx="3" fill="#d0d0d0" />
    <rect x="8" y="38" width="42" height="5" rx="2.5" fill="#e0e0e0" />
    <rect x="8" y="49" width="36" height="5" rx="2.5" fill="#e0e0e0" />
    <rect x="8" y="60" width="28" height="5" rx="2.5" fill="#e0e0e0" />
    {/* Dark half (right) */}
    <rect x="55" width="55" height="78" fill="#1e1e1e" />
    <rect x="55" width="55" height="18" fill="#2d2d2d" />
    <circle cx="70" cy="9" r="4" fill="#28c840" />
    <rect x="62" y="26" width="34" height="6" rx="3" fill="#4a4a4a" />
    <rect x="62" y="38" width="42" height="5" rx="2.5" fill="#3a3a3a" />
    <rect x="62" y="49" width="36" height="5" rx="2.5" fill="#3a3a3a" />
    <rect x="62" y="60" width="28" height="5" rx="2.5" fill="#3a3a3a" />
    {/* Divider */}
    <line x1="55" y1="0" x2="55" y2="78" stroke="#9e9e9e" strokeWidth="1" />
  </svg>
);

const OPTIONS: { mode: ThemeMode; label: string; Thumbnail: FC; testId: string }[] = [
  { mode: 'light', label: 'Light', Thumbnail: LightThumbnail, testId: 'theme-option-light' },
  { mode: 'dark', label: 'Dark', Thumbnail: DarkThumbnail, testId: 'theme-option-dark' },
  { mode: 'auto', label: 'Auto', Thumbnail: AutoThumbnail, testId: 'theme-option-auto' },
];

interface ThemeModeControlProps {
  /** Optional scope identifier — for future use when enabling dark mode in other layouts */
  scope?: string;
  /** Whether to show the control */
  visible?: boolean;
}

const ThemeModeControl: FC<ThemeModeControlProps> = ({ visible = true }) => {
  const { themeMode, setThemeMode } = useThemeMode();

  if (!visible) return null;

  return (
    <AppearanceContainer elevation={4} data-testid="theme-mode-control">
      <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
        Appearance
      </Typography>
      <AppearanceCardsRow>
        {OPTIONS.map(({ mode, label, Thumbnail, testId }) => (
          <AppearanceCard
            key={mode}
            selected={themeMode === mode}
            onClick={() => setThemeMode(mode)}
            data-testid={testId}
            aria-label={`${label} mode`}
            aria-pressed={themeMode === mode}
          >
            <AppearanceThumbnail>
              <Thumbnail />
            </AppearanceThumbnail>
            <Typography
              variant="body2"
              color={themeMode === mode ? 'text.primary' : 'text.secondary'}
              fontWeight={themeMode === mode ? 700 : 400}
            >
              {label}
            </Typography>
          </AppearanceCard>
        ))}
      </AppearanceCardsRow>
    </AppearanceContainer>
  );
};

export default ThemeModeControl;
